import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  hasFeature as hasFeatureTool,
  processUserFeatures,
} from "@/users/v3/useGetUserProperties/helpers";
import { UserProperties } from "@/types/models/User/v3";
import featuresQueries from "@/users/v3/features";

export interface UseGetUserPropertiesReturn extends UserProperties {
  isLoadingFeatures: boolean;
  errorFeatures: Error | null;
}

/**
 * useGetUserProperties() - Custom Hook to get user properties
 * @description Returns the properties of a user based on the user features that are defined in the backend.
 * Visit: https://localhost/admin/api-tool-demo/get-user-properties/ to see some live demos containing the returned data.
 *
 * @example
 * ```typescript
 * const {
 *   canAccessImplementationGuide,
 *   canAdminMakeChanges,
 *   canAdminViewUsageReport,
 *   canCreateClassrooms,
 *   canModifyContentSettings,
 *   canPrintPremiumContent,
 *   canSeeEverwrite,
 *   canSeeFormative,
 *   canStartPremiumTrial,
 *   canSyncClassrooms,
 *   canViewAdminConsole,
 *   canViewContentSettings,
 *   canViewPowerWords,
 *   canViewSearch,
 *   canViewTheirLicenses,
 *   hadPremiumTrialCampaignExtensionThisSchoolYear,
 *   hadPremiumTrialThisSchoolYear,
 *   hadPremiumTrialThisYear,
 *   hadSchoolPocLicense,
 *   hasAccessToLite,
 *   hasActivePremiumTrial,
 *   hasActivePremiumTrialCampaign,
 *   hasActivePremiumTrialCampaignExtension,
 *   hasBulkClassrooms,
 *   hasNceTrialCampaign,
 *   hasPaidEssentialsLicense,
 *   hasPremiumLicense,
 *   hasSchoolPocLicense,
 *   isFirstMonthUser,
 *   isFirstWeekUser,
 *   isImpersonated,
 *   isLiteUser,
 *   isNewUser,
 *   isSchoolEducator,
 *   isStudentInDistrictWithAllAdvisoryContent,
 *   needsInitialOnboarding,
 *   needsOnboarding,
 *   needsReturningOnboarding,
 * } = useGetUserProperties();
 * ```
 *
 * @param {String} uniqueId - The user PK - Primary Key, if not sent, the current user will be used as "me"
 * @returns {Partial<UseGetUserPropertiesReturn>}
 * @tutorial https://newsela.atlassian.net/wiki/spaces/UM/pages/4175167491/v3+User+Lyceum+Tools
 */
const useGetUserProperties = (
  uniqueId: string | "me" | null = "me",
): Partial<UseGetUserPropertiesReturn> => {
  const {
    data: features,
    isLoading: isLoadingFeatures,
    error: errorFeatures,
  } = useQuery({
    ...featuresQueries.filter(uniqueId!!),
  });

  const [properties, setProperties] = useState<
    Partial<UseGetUserPropertiesReturn>
  >({
    isLoadingFeatures,
    errorFeatures,
  });

  useEffect(() => {
    if (!isLoadingFeatures && features) {
      setProperties((prevState) => ({
        ...prevState,
        hasFeature: (featureName: string) =>
          hasFeatureTool(featureName, features),
      }));

      setProperties((prevState) => {
        const featureBasedProps = processUserFeatures(
          (featureName: string) => hasFeatureTool(featureName, features),
          features,
        );

        return {
          ...prevState,
          ...featureBasedProps,
        };
      });
    }

    setProperties((prevState) => ({
      ...prevState,
      errorFeatures,
      isLoadingFeatures,
      // Only sets isAuthenticatedUser if the user is "me"
      isAuthenticatedUser:
        uniqueId === "me" ? !!prevState.isAuthenticatedUser : false,
    }));
  }, [uniqueId, features, isLoadingFeatures, errorFeatures]);

  return properties;
};

export default useGetUserProperties;
